import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Dfns from 'date-fns/fp';
import { MdDeleteForever } from 'react-icons/md';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { DatePicker } from '@Components/DatePicker';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { IconButton } from '@Components/IconButton';
import { Page } from '@Components/Page';
import { BPMTable } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { useSetError } from '@src/redux/modals';
import { download } from '@src/utils/download-utils';
import { isNotMonday } from '@src/utils/date-utils';
import {
  buildCreditMemoHeaders,
  DATE_FORMAT,
  HEADER_HEIGHT,
  PAGE_TITLE,
  PAGE_TYPE,
  ROW_HEIGHT
} from './credit-memos-constants';
import { CreditMemoModal } from './CreditMemoModal';
import {
  CreditMemoRow,
  CreditMemoStatus,
  CreditMemoStatusType,
  getSelectionDisabledReason,
  stripCsvColumns
} from './credit-memos-utils';
import {
  creditMemosQueryKey,
  deleteCreditMemo,
  downloadCreditMemoPDF,
  patchCreditMemoStatus,
  useCreditMemos
} from './use-credit-memos';

const CSV_MENU_OPTIONS = ['CSV'];

export const CreditMemos = (): JSX.Element => {
  const setError = useSetError();
  const queryClient = useQueryClient();
  // Defaults to the last month, ending with the current week. Both bounds are Monday
  // (startOfISOWeek), matching every other fullWeeksOnly DatePicker caller (LinearPlans,
  // LinearBuying) and the "Week of <Monday>" label the picker itself always renders.
  const [startWeek, setStartWeek] = useState(
    Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(Dfns.subMonths(1, new Date())))
  );
  const [endWeek, setEndWeek] = useState(Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(new Date())));
  const queryKey = useMemo(() => creditMemosQueryKey(startWeek, endWeek), [startWeek, endWeek]);
  const { data: fetchedData, isLoading, error } = useCreditMemos(startWeek, endWeek);
  // Local editable working copy: rows carry client-only selection/fetching state and get
  // patched/deleted in place, so we layer that on top of the query result rather than mutating
  // the cache directly.
  const [data, setData] = useState<CreditMemoRow[]>([]);
  const [csvContent, setCsvContent] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPatching, setIsPatching] = useState(false);
  const [deleteRow, setDeleteRow] = useState<CreditMemoRow>();

  // Re-fires whenever anything writes to the query cache -- not just a genuine refetch --
  // since updateCreditMemoRow/handleDeleteCreditMemo both call queryClient.setQueryData to keep
  // the cache in sync (see their own comments). The cache-derived shape never carries local-only
  // fields (`selected`, `fetching`), so merge them in from the current row by id rather than
  // overwriting outright -- otherwise patching/deleting one row would reset every OTHER row's
  // selection or in-flight PDF-download state. A genuine date-range change naturally has no id
  // overlap with the prior range, so this still starts every new row fresh.
  useEffect(() => {
    setData(current => {
      const currentById = new Map(current.map(row => [row.id, row]));
      return (fetchedData ?? []).map(row => {
        const existing = currentById.get(row.id);
        return existing
          ? { ...row, selected: existing.selected, fetching: existing.fetching }
          : row;
      });
    });
  }, [fetchedData]);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load credit memos",
        message: `Failed to load data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // Patches both the local working copy and the React Query cache entry for the current date
  // range, so a stale-but-not-yet-refetched cache hit (e.g. switching the range away and back
  // within the query's staleTime) doesn't resurrect the pre-mutation row.
  const updateCreditMemoRow = useCallback(
    (id: number, changes: Partial<CreditMemoRow>): void => {
      const apply = (rows: CreditMemoRow[] | undefined) =>
        rows?.map(row => (row.id === id ? { ...row, ...changes } : row));
      setData(current => apply(current) ?? current);
      queryClient.setQueryData<CreditMemoRow[]>(queryKey, apply);
    },
    [queryClient, queryKey]
  );

  // Client-only UI state -- unlike status/amount changes, there's no server record of a
  // selection to keep in sync, so this only ever touches the local working copy.
  const handleToggleSelect = useCallback((row: CreditMemoRow): void => {
    setData(current => current.map(r => (r.id === row.id ? { ...r, selected: !r.selected } : r)));
  }, []);

  // Returns whether the patch actually succeeded, so callers (e.g. CreditMemoModal's send flow)
  // can decide whether it's safe to treat the overall action as complete -- this already surfaces
  // its own error dialog on failure, so callers shouldn't show a second one on top of it.
  const handlePatchCreditMemo = useCallback(
    async (row: CreditMemoRow, status: CreditMemoStatusType): Promise<boolean> => {
      setIsPatching(true);
      try {
        await patchCreditMemoStatus(row.id, status);
        // Move outstanding credit to approved credit on approval.
        updateCreditMemoRow(row.id, {
          status,
          ...(status === CreditMemoStatus.ACCEPTED
            ? { approvedCreditAmount: row.creditAmount, creditAmount: 0 }
            : {})
        });
        return true;
      } catch (e) {
        setError({ message: e.message, reportError: e });
        return false;
      } finally {
        setIsPatching(false);
      }
    },
    [setError, updateCreditMemoRow]
  );

  const handleDeleteCreditMemo = useCallback(
    async (row: CreditMemoRow): Promise<void> => {
      setIsDeleting(true);
      try {
        await deleteCreditMemo(row.id);
        const remove = (rows: CreditMemoRow[] | undefined) => rows?.filter(r => r.id !== row.id);
        setData(current => remove(current) ?? current);
        queryClient.setQueryData<CreditMemoRow[]>(queryKey, remove);
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setIsDeleting(false);
      }
    },
    [setError, queryClient, queryKey]
  );

  const handleDownloadPDF = useCallback(
    async (row: CreditMemoRow): Promise<void> => {
      setData(current => current.map(r => (r.id === row.id ? { ...r, fetching: true } : r)));
      try {
        await downloadCreditMemoPDF(row);
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setData(current => current.map(r => (r.id === row.id ? { ...r, fetching: false } : r)));
      }
    },
    [setError]
  );

  const selectedRows = useMemo(() => data.filter(row => row.selected), [data]);
  const isOneRowSelected = selectedRows.length === 1;
  const disabledReason = getSelectionDisabledReason(selectedRows);
  const isActionDisabled = Boolean(disabledReason);
  const disabledTooltip = useMemo(
    () => (disabledReason ? { text: disabledReason } : undefined),
    [disabledReason]
  );

  // Depends only on the two stable handlers -- BPMTable keys its memoized cell renderers and CSV
  // builder off this array's identity, so a per-row transient flag (fetching, selected) lives on
  // the row's own `data` entry instead of here, or every column would be invalidated by a single
  // PDF download starting or a single checkbox click.
  const creditMemoHeaders = useMemo(
    () =>
      buildCreditMemoHeaders({
        onToggleSelect: handleToggleSelect,
        onDownloadPDF: handleDownloadPDF
      }),
    [handleToggleSelect, handleDownloadPDF]
  );

  const onCsvChange = useCallback((csv: string) => setCsvContent(csv), []);

  // BPMTable's own CSV builder has no way to exclude a column, so strip the non-data ones
  // (checkbox toggle, PDF download button) at download time -- rather than on every table-data
  // change, which used to re-parse/re-serialize the whole CSV on something as small as a single
  // checkbox click -- matches what the page's own CSV export excluded before this was rebuilt on
  // BPMTable's onCsvChange.
  const downloadCsv = useCallback(() => {
    const strippedCsv = stripCsvColumns(csvContent, creditMemoHeaders, ['selected', 'pdf']);
    download(strippedCsv, `Credit Memos ${startWeek} - ${endWeek}.csv`, 'text/csv');
  }, [csvContent, creditMemoHeaders, startWeek, endWeek]);
  const csvClickOptions = useMemo(() => [downloadCsv], [downloadCsv]);

  const renderTableLoading = useCallback(
    () => (
      <TableLoading
        isLoading={isLoading}
        error={error}
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        emptyMessage='No rows to show in specified date range.'
      />
    ),
    [isLoading, error]
  );

  // The date range is a page-level concern (matches LinearPlans, which also puts its date
  // range in Page's `actions`, top-right of the page header) rather than a table control.
  const pageActions = useMemo(
    () => (
      <DatePicker
        range={{ start: startWeek, end: endWeek }}
        fullWeeksOnly
        isOutsideRange={() => false}
        isDayBlocked={isNotMonday}
        onChange={({ start, end }) => {
          if (start && end) {
            setStartWeek(start);
            setEndWeek(end);
          }
        }}
      />
    ),
    [startWeek, endWeek]
  );

  // The row-selection actions sit here as BPMTable's `additionalControls`, alongside its own
  // built-in filter bar (`filterBar`, derived from `creditMemoHeaders`) -- matches
  // LinearCpms/UserAdmin/AccountPlanning's single-table shape. The filter bar itself grows to
  // fill the row (its own `auto-width` class sets `flex: 1`), pushing these controls to the end
  // of the row -- Download last, so it lands all the way on the right, matching LinearCpms.
  const tableControls = useMemo(
    () => (
      <>
        <Button
          size={Size.LG}
          data-testid='accept-button'
          design={Intent.SUCCESS}
          appearance={Appearance.OUTLINED}
          disabled={isActionDisabled}
          loading={isPatching}
          tooltip={disabledTooltip}
          onClick={() => handlePatchCreditMemo(selectedRows[0], CreditMemoStatus.ACCEPTED)}
        >
          Accepted
        </Button>
        <Button
          size={Size.LG}
          data-testid='reject-button'
          design={Intent.ERROR}
          appearance={Appearance.OUTLINED}
          disabled={isActionDisabled}
          loading={isPatching}
          tooltip={disabledTooltip}
          onClick={() => handlePatchCreditMemo(selectedRows[0], CreditMemoStatus.REJECTED)}
        >
          Rejected
        </Button>
        <Button
          size={Size.LG}
          data-testid='email-credit-memo-button'
          disabled={isActionDisabled}
          tooltip={disabledTooltip}
          onClick={() => setShowSendModal(true)}
        >
          Email Credit Memo
        </Button>
        <IconButton
          data-testid='delete-trigger-button'
          disabled={!isOneRowSelected}
          loading={isDeleting}
          onClick={() => setDeleteRow(selectedRows[0])}
        >
          <MdDeleteForever />
        </IconButton>
        <div data-testid='download-dropdown'>
          <DownloadDropdown
            disabled={isLoading || data.length === 0}
            menuOptions={CSV_MENU_OPTIONS}
            onClickOptions={csvClickOptions}
          />
        </div>
      </>
    ),
    [
      isActionDisabled,
      disabledTooltip,
      isPatching,
      selectedRows,
      handlePatchCreditMemo,
      isOneRowSelected,
      isDeleting,
      isLoading,
      data,
      csvClickOptions
    ]
  );

  return (
    <Page title={PAGE_TITLE} pageType={PAGE_TYPE} isLoading={isLoading} actions={pageActions}>
      <TablePage tableClassName='credit-memos-table' dataTestId='credit-memos-table'>
        <BPMTable
          alternateColors
          headers={creditMemoHeaders}
          data={data}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          onCsvChange={onCsvChange}
          noRowsRenderer={renderTableLoading}
          additionalControls={tableControls}
        />
      </TablePage>
      {showSendModal && isOneRowSelected && (
        <CreditMemoModal
          row={selectedRows[0]}
          patchCreditMemo={handlePatchCreditMemo}
          show={showSendModal}
          onHide={() => setShowSendModal(false)}
        />
      )}
      {deleteRow && (
        <AlertDialog
          show
          onHide={() => setDeleteRow(undefined)}
          intent={Intent.ERROR}
          title='Delete Credit Memo'
          body={`Are you sure you want to delete the credit memo for ${deleteRow.company}, ${deleteRow.network}, ${deleteRow.avail}, ${deleteRow.week}?`}
          primaryAction={{ onClick: () => handleDeleteCreditMemo(deleteRow), label: 'Delete' }}
        />
      )}
    </Page>
  );
};
